import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { floors } from "../../api/endpoints/floors";
import { qk } from "../../api/queryKeys";
import { FormShell } from "../../components/forms/FormShell";
import { NumberField } from "../../components/forms/NumberField";
import { TextField } from "../../components/forms/TextField";
import { slugify } from "../../lib/slug";
import type { FloorCreate } from "../../api/types/floor";

interface FloorCreateFormProps {
  accountSlug: string;
  tenantSlug: string;
  siteSlug: string;
  onDone: () => void;
  onPendingChange?: (pending: boolean) => void;
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;
const EMPTY = { slug: "", label: "", level: 0 as number | null };

export function FloorCreateForm({
  accountSlug,
  tenantSlug,
  siteSlug,
  onDone,
  onPendingChange,
}: FloorCreateFormProps) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState(EMPTY);
  const slugTouched = useRef(false); // see lib/slug.ts + AccountCreateForm.

  const slugError = useMemo(() => {
    if (!values.slug) return null;
    return SLUG_RE.test(values.slug) ? null : "Invalid slug.";
  }, [values.slug]);

  const mutation = useMutation({
    mutationFn: () => {
      const body: FloorCreate = {
        slug: values.slug,
        label: values.label,
        level: values.level ?? 0,
      };
      return floors.create(accountSlug, tenantSlug, siteSlug, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.floors(accountSlug, tenantSlug, siteSlug),
      });
      setValues(EMPTY);
      slugTouched.current = false;
      onDone();
    },
  });

  useEffect(() => {
    onPendingChange?.(mutation.isPending);
  }, [mutation.isPending, onPendingChange]);

  const isValid =
    values.slug !== "" &&
    SLUG_RE.test(values.slug) &&
    values.label !== "" &&
    values.level !== null;

  return (
    <FormShell
      submitLabel="Create floor"
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValid) return;
        mutation.mutate();
      }}
      onCancel={onDone}
      pending={mutation.isPending}
      error={mutation.error}
      errorTitle="Create floor failed"
    >
      <TextField
        label="Label"
        value={values.label}
        onChange={(label) =>
          setValues((v) => ({
            ...v,
            label,
            slug: slugTouched.current ? v.slug : slugify(label),
          }))
        }
        required
        placeholder="Ground floor"
        helper="The human-readable label. Slug below is auto-derived."
      />
      <TextField
        label="Slug"
        value={values.slug}
        onChange={(slug) => {
          slugTouched.current = true;
          setValues((v) => ({ ...v, slug }));
        }}
        required
        placeholder="ground"
        helper="Auto-derived from label until you edit it. There is no rename — typos stick."
        error={slugError}
      />
      <NumberField
        label="Level"
        value={values.level}
        onChange={(level) => setValues((v) => ({ ...v, level }))}
        required
        helper="0 = ground, -1 = basement, 1 = first floor, …"
        step={1}
      />
    </FormShell>
  );
}
